import { detectIdentifierType } from '../../utils/validations';
import { EnrichmentOrder, EnrichmentOrderBy } from './types';

export const parseRatioToNumber = (r: string | number | undefined): number => {
  if (r === undefined || r === null) return 0;
  if (typeof r === 'number') return Number.isFinite(r) ? r : 0;
  const asStr = String(r).trim();
  if (asStr.includes('/')) {
    const [numS, denS] = asStr.split('/').map(s => s.trim());
    const num = parseFloat(numS);
    const den = parseFloat(denS);
    if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return 0;
    return num / den;
  }
  const parsed = parseFloat(asStr);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getColorByLogP = (logP: number): string => {
  if (logP >= 8) return '#fc0314';
  if (logP <= 2) return '#1e01fd';
  if (logP <= 5) {
    const t = (logP - 2) / 3;
    return `rgb(${Math.round(30 + (197 - 30) * t)}, ${Math.round(1 + (0 - 1) * t)}, ${Math.round(253 + (143 - 253) * t)})`;
  }
  const t = (logP - 5) / 3;
  return `rgb(${Math.round(197 + (252 - 197) * t)}, ${Math.round(0 + (3 - 0) * t)}, ${Math.round(143 + (20 - 143) * t)})`;
};

export const tabs = ['GENES', 'VARIANTS'];

export const isDseaEnabled = process.env.REACT_APP_DSEA_ENABLED === 'true';

export const isGseaEnabled = process.env.REACT_APP_GSEA_ENABLED === 'true';

export const analysisMethodOptions = [
  {
    value: 'FISHER' as const,
    label: 'ORA (Over-representation analysis)',
    description: 'Tests whether disease-associated genes overlap significantly with your input list.',
  },
  {
    value: 'GSEA' as const,
    label: 'DSEA (DISGENET Score Enrichment Analysis)',
    description: 'Ranks genes by DISGENET association score and computes a running-sum enrichment statistic.',
  },
  {
    value: 'BOTH' as const,
    label: 'Run both and compare',
    description: 'Runs ORA first, then DSEA on the same gene list for side-by-side comparison.',
  },
];

export const getAnalysisMethodOptions = () =>
  isDseaEnabled ? analysisMethodOptions : analysisMethodOptions.filter(option => option.value === 'FISHER');

export const defaultGseaParams = {
  source: 'ALL',
  minScore: 0,
  exponent: 1,
  minCommonGenes: 2,
  minGeneSetSize: 15,
  maxGeneSetSize: 5000,
  minHits: 5,
  nPermutations: 1,
};

export const GSEA_MAX_GENES = 4000;

export const ENRICHMENT_GENE_SCORE_MAX = 1.55;
export const ENRICHMENT_VARIANT_SCORE_MAX = 1.2;

export const getEnrichmentScoreMax = (searchType?: string): number =>
  searchType === 'VARIANTS' ? ENRICHMENT_VARIANT_SCORE_MAX : ENRICHMENT_GENE_SCORE_MAX;

export const DEFAULT_ENRICHMENT_ORDER_BY = 'pvalue' as const;
export const DEFAULT_ENRICHMENT_ORDER = 'ASC' as const;

export const defaultEnrichmentOrderForColumn = (column: EnrichmentOrderBy): EnrichmentOrder =>
  column === 'pvalue' ? 'ASC' : 'DESC';

/** Normalize MeSH disease class names from API (array, pipe/comma string, or missing). */
export const normalizeDiseaseClassNames = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return [...new Set(value.map(item => String(item).trim()).filter(Boolean))];
  }
  if (typeof value === 'string' && value.trim()) {
    return [
      ...new Set(
        value
          .split(/[|;,]/)
          .map(item => item.trim())
          .filter(Boolean),
      ),
    ];
  }
  return [];
};

export const collectDiseaseClassOptions = (
  items: { diseaseClassName_MSH?: string[] }[],
): string[] => {
  const names = new Set<string>();
  items.forEach(item => {
    (item.diseaseClassName_MSH ?? []).forEach(name => names.add(name));
  });
  return [...names].sort((a, b) => a.localeCompare(b));
};

export const matchesDiseaseClassFilter = (
  item: { diseaseClassName_MSH?: string[] },
  selectedClass: string,
): boolean => {
  const selected = selectedClass.trim();
  if (!selected) return true;
  return (item.diseaseClassName_MSH ?? []).includes(selected);
};

export const geneFilterOptions = [
  { value: 'geneSymbol', label: 'Gene symbol' },
  { value: 'ensemblId', label: 'Ensembl ID' },
  { value: 'uniprotId', label: 'UniProt ID' },
  { value: 'geneId', label: 'NCBI Gene ID' },
];

const ENRICHMENT_TO_ADVANCED_SEARCH_GENE_FILTER: Record<string, string> = {
  geneSymbol: 'name',
  symbol: 'name',
  name: 'name',
  ensemblId: 'ensemblID',
  ensemblID: 'ensemblID',
  uniprotId: 'uniprotID',
  uniprotID: 'uniprotID',
  geneId: 'geneID',
  geneID: 'geneID',
};

export const getAdvancedSearchFilterForUnresolvedGenes = (
  unresolvedItems: string[],
  geneFilterType: string,
): string => {
  const detectedType = detectIdentifierType(unresolvedItems.join('\n'));
  const sourceType = detectedType ?? geneFilterType;
  return ENRICHMENT_TO_ADVANCED_SEARCH_GENE_FILTER[sourceType] ?? 'name';
};
